import { Link } from "react-router-dom";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

export type AdSize =
  | "leaderboard"
  | "rectangle"
  | "halfpage"
  | "inline"
  | "mobile-banner";

export type AdStatus = "active" | "scheduled" | "expired" | "paused";

export interface AdPlacement {
  id: string;
  label: string;
  page: string;
  size: AdSize;
  active: boolean;
  brand: string;
  tagline: string;
  cta: string;
  ctaUrl: string;
  image: string;
  startDate?: string;
  endDate?: string;
}

interface AdBannerProps {
  placementId: string;
  /** The slot's physical size on the page — wins over whatever is stored on the row. */
  size?: AdSize;
  className?: string;
  /** Rendered when the slot has no live ad (e.g. an "advertise here" card). */
  fallback?: ReactNode;
}

function AdCta({ href, className, children }: { href: string; className: string; children: ReactNode }) {
  // Advertiser links are usually external; react-router's <Link> would turn
  // "https://brand.com" into a broken in-site path.
  if (/^(https?:)?\/\//i.test(href) || /^(mailto|tel):/i.test(href)) {
    return <a href={href} target="_blank" rel="noopener sponsored" className={className}>{children}</a>;
  }
  return <Link to={href} className={className}>{children}</Link>;
}

const sizeClasses: Record<AdSize, string> = {
  leaderboard: "w-full h-[90px]",
  rectangle: "w-full h-[250px]",
  halfpage: "w-full h-[600px]",
  inline: "w-full h-[120px]",
  "mobile-banner": "w-full h-[80px]",
};

function mapRow(row: Record<string, unknown>): AdPlacement {
  return {
    id: row.id as string,
    label: row.label as string,
    page: row.page as string,
    size: row.size as AdSize,
    active: row.active as boolean,
    brand: row.brand as string,
    tagline: row.tagline as string,
    cta: row.cta as string,
    ctaUrl: (row.cta_url as string) || "/advertising",
    image: row.image as string,
    startDate: row.start_date as string | undefined,
    endDate: row.end_date as string | undefined,
  };
}

function getPlacementStatus(p: AdPlacement): AdStatus {
  if (!p.active) return "paused";
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (p.endDate) {
    const end = new Date(p.endDate);
    end.setHours(23, 59, 59, 999);
    if (now > end) return "expired";
  }

  if (p.startDate) {
    const start = new Date(p.startDate);
    start.setHours(0, 0, 0, 0);
    if (now < start) return "scheduled";
  }

  return "active";
}

function isPlacementLive(p: AdPlacement): boolean {
  return getPlacementStatus(p) === "active";
}

export default function AdBanner({ placementId, size: slotSize, className = "", fallback = null }: AdBannerProps) {
  const { t } = useTranslation();
  const [placement, setPlacement] = useState<AdPlacement | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPlacement = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("ad_placements")
        .select("*")
        .eq("id", placementId)
        .maybeSingle();
      if (!error && data) {
        setPlacement(mapRow(data));
      } else {
        setPlacement(null);
      }
    } catch {
      setPlacement(null);
    } finally {
      setLoading(false);
    }
  }, [placementId]);

  useEffect(() => {
    fetchPlacement();
    const handleUpdate = () => fetchPlacement();
    window.addEventListener("alsiyahiah:adplacements:updated", handleUpdate);
    // Refresh schedules and changes made by an administrator in another session.
    const interval = window.setInterval(handleUpdate, 60_000);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("alsiyahiah:adplacements:updated", handleUpdate);
    };
  }, [fetchPlacement]);

  // لا تعرض الإعلان إذا لم يُعثر على الموضع أو لم يكن مباشراً
  if (loading) return null;
  if (!placement || !isPlacementLive(placement)) return <>{fallback}</>;

  const size: AdSize = slotSize ?? (sizeClasses[placement.size] ? placement.size : "inline");
  const isLeaderboard = size === "leaderboard";
  const isInline = size === "inline";
  const isMobile = size === "mobile-banner";
  const isRectangle = size === "rectangle";
  const isHalfpage = size === "halfpage";
  const sizeClass = sizeClasses[size];

  return (
    <div
      className={`relative overflow-hidden rounded-xl group ${sizeClass} ${className}`}
      role="complementary"
      aria-label={t("ads.label")}
    >
      <img
        src={placement.image}
        alt={placement.brand}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-l from-black/80 via-black/50 to-black/20" />

      <span className="absolute top-2 right-2 bg-black/40 backdrop-blur-sm text-white/60 text-[10px] px-1.5 py-0.5 rounded font-medium tracking-wider z-10">
        {t("ads.label")}
      </span>

      <div
        className={`absolute inset-0 flex items-center z-10 ${
          isLeaderboard || isInline || isMobile
            ? "flex-row justify-between px-5 md:px-8 gap-4"
            : "flex-col justify-end p-5"
        }`}
      >
        {(isLeaderboard || isInline || isMobile) && (
          <>
            <div className="flex-1 min-w-0">
              <p className={`font-bold text-white font-['Cairo'] truncate ${isMobile ? "text-sm" : isLeaderboard ? "text-base" : "text-lg"}`}>
                {placement.brand}
              </p>
              {!isMobile && (
                <p className={`text-white/70 mt-0.5 truncate ${isLeaderboard ? "text-xs" : "text-sm"}`}>
                  {placement.tagline}
                </p>
              )}
            </div>
            <AdCta
              href={placement.ctaUrl || "/advertising"}
              className={`shrink-0 bg-[#D4AF37] text-[#0A1628] font-bold rounded-full hover:bg-[#c49f30] transition-colors cursor-pointer whitespace-nowrap ${
                isMobile ? "text-xs px-3 py-1.5" : isLeaderboard ? "text-xs px-4 py-2" : "text-sm px-5 py-2.5"
              }`}
            >
              {placement.cta}
            </AdCta>
          </>
        )}

        {(isRectangle || isHalfpage) && (
          <>
            {isHalfpage && (
              <div className="flex-1 flex items-start pt-4">
                <div className="bg-[#D4AF37] text-[#0A1628] text-xs font-bold px-2.5 py-1 rounded-full">{t("ads.label")}</div>
              </div>
            )}
            <div>
              <p className="text-[#D4AF37] text-xs font-bold mb-1 uppercase tracking-wider">{placement.brand}</p>
              <p className={`text-white font-bold font-['Cairo'] leading-snug mb-3 ${isHalfpage ? "text-xl" : "text-sm"}`}>
                {placement.tagline}
              </p>
              <AdCta
                href={placement.ctaUrl || "/advertising"}
                className="inline-block bg-[#D4AF37] text-[#0A1628] text-xs font-bold px-4 py-2 rounded-full hover:bg-[#c49f30] transition-colors cursor-pointer whitespace-nowrap"
              >
                {placement.cta}
              </AdCta>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
